import TableContainer from 'components/common/TableContainer';
import React, { useEffect, useMemo, useState } from 'react';
import {
  BasicMonth,
  CtcPerMonth,
  EmployeeId,
  EmployeeName,
  HraMonth,
  AdocAndVariable,
  LopDays,
  NetPay,
  Status,
} from './PayoutCol';
import httpInjectorService from 'services/http-injector.service';
import { Button, Checkbox ,Select as ChakraSelect,  Badge } from '@chakra-ui/react';
import { Empty } from 'antd';
import { Label } from 'reactstrap';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { BulletList } from 'react-content-loader';
import { demoPayoutData } from 'components/demodata/Dummydata';

const Payout = ({ activeTab }) => {
  const [getPayoutData, setPayoutData] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const [isloading, setIsLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format('MMMM'));
  const [selectedYear, setSelectedYear] = useState(dayjs().format('YYYY'));
  const [selectedStatus, setSelectedStatus] = useState('');
  const [editingRow, setEditingRow] = useState(null);
  const [buttonLoader, setButtonLoader] = useState(false);

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const years = Array.from({ length: 10 }, (_, i) =>
    (dayjs().year() - 5 + i).toString(),
  );

const toggleAllSelection = () => {
  const selectableUserIds = getPayoutData
    .filter((item) => item.status !== 'SALARY ON HOLD')
    .map((item) => item.user_id);

  setSelectedUsers((prevSelectedUsers) => {
    const allAlreadySelected = selectableUserIds.every((id) =>
      prevSelectedUsers.includes(id)
    );
    return allAlreadySelected ? [] : selectableUserIds;
  });

  setAllSelected((prevAllSelected) => !prevAllSelected);
};

  const toggleRowSelection = (id) => {
    setSelectedUsers((prevState) => {
      if (prevState.includes(id)) {
        return prevState.filter((rowId) => rowId !== id);
      } else {
        return [...prevState, id];
      }
    });
  };
  

  const columns = useMemo(
    () => [
     {
  Header: (
    <Checkbox
      colorScheme="purple"
      isChecked={allSelected}
      onChange={toggleAllSelection}
    />
  ),
  accessor: 'select',
  Cell: ({ row }) =>
    row.original.status === 'SALARY ON HOLD' ? null : (
      <Checkbox
        colorScheme="purple"
        isChecked={selectedUsers?.includes(row.original.user_id)}
        onChange={() => toggleRowSelection(row.original.user_id)}
      />
    ),
},
      {
        Header: 'Employee ID',
        accessor: 'employee_id',
        disableFilters: true,
        Cell: (cellProps) => <EmployeeId {...cellProps} />,
      },
      {
        Header: 'Employee Name',
        accessor: 'name',
        disableFilters: true,
        Cell: (cellProps) => <EmployeeName {...cellProps} />,
      },
      {
        Header: 'Adoc / Variable',
        accessor: 'adoc_variable_amount' ,
        disableFilters: true,
        Cell: (cellProps) => <AdocAndVariable {...cellProps} />,
      },
      {
        Header: 'HRA Month',
        accessor: 'hra_month',
        disableFilters: true,
        Cell: (cellProps) => <HraMonth {...cellProps} />,
      },
      {
        Header: 'Basic Month',
        accessor: 'basic_month',
        disableFilters: true,
        Cell: (cellProps) => <BasicMonth {...cellProps} />,
      },
      {
        Header: 'Month CTC',
        accessor: 'ctc_per_month',
        disableFilters: true,
        Cell: (cellProps) => <CtcPerMonth {...cellProps} />,
      },
      {
        Header: 'LOP Days',
        accessor: 'lop',
        disableFilters: true,
        Cell: (cellProps) => <LopDays {...cellProps} />,
      },
      {
        Header: 'Net Pay',
        accessor: 'net_pay',
        disableFilters: true,
        Cell: (cellProps) => <NetPay {...cellProps} />,
      },
      {
        Header: 'Status',
        accessor: 'status',
      
        Cell: ({ row }) => {
          const currentStatus = row.original.status;
      
          const statusColors = {
            'SALARY ON HOLD': 'red',
            PENDING: 'orange',
            PAID: 'green',
          };
      
          // EDIT MODE
          if (editingRow === row.original.id) {
            return (
              <ChakraSelect
                size="sm"
                value={currentStatus}
                autoFocus
                onChange={(e) =>
                  handleChangeStatus(row, e.target.value)
                }
                onBlur={() => setEditingRow(null)}
                borderColor={`${statusColors[currentStatus]}.300`}
                focusBorderColor={`${statusColors[currentStatus]}.500`}
                bg={`${statusColors[currentStatus]}.50`}
                fontWeight="600"
              >
                <option value="SALARY ON HOLD">
                  SALARY ON HOLD
                </option>
      
                <option value="PENDING">
                  PENDING
                </option>
      
                <option value="PAID">
                  PAID
                </option>
              </ChakraSelect>
            );
          }
      
          // BADGE MODE
          return (
            <Badge
              colorScheme={statusColors[currentStatus]}
              cursor="pointer"
              px={3}
              py={1}
              borderRadius="md"
              onClick={() => setEditingRow(row.original.id)}
            >
              {currentStatus}
            </Badge>
          );
        },
      }
    ],
    [allSelected, selectedUsers, getPayoutData, editingRow],
  );

  const handleChangeStatus = async (row, value) => {
    if (!value || value === row.original.status) {
      setEditingRow(null);
      return;
    }
  
    try {
      const reqBody = {
        row_id: row.original.id,
        status: value,
      };
  
      const response =
        await httpInjectorService.updatePayoutDetails(reqBody);
  
      if (response.status === 'success') {
        toast.success(response.message, {
          autoClose: 1000,
          position: 'top-right',
        });
  
        // UPDATE TABLE INSTANTLY
        setPayoutData((prev) =>
          prev.map((item) =>
            item.id === row.original.id
              ? { ...item, status: value }
              : item
          )
        );
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setEditingRow(null);
    }
  };

  const getPayoutDetails = async () => {
    const reqBody = {
      month: selectedMonth,
      year: selectedYear,
    };
    setIsLoading(true);
    try {
    const isDemoMode = localStorage.getItem("demoMode") === "true";

    if (isDemoMode) {
      setPayoutData(demoPayoutData);
      setIsLoading(false);
      return;
    }
      const response = await httpInjectorService.getPayoutDetails(reqBody);
      if (response.status === 'success') {
        setPayoutData(response.data);
        setIsLoading(false);
        toast.success(response.message, {
          autoClose: 1000,
          position: 'top-right',
        });
      } else {
        setPayoutData([]);
        setIsLoading(false);
        toast.error(response.message, {
          autoClose: 1000,
          position: 'top-right',
        });
      }
    } catch (err) {
      setPayoutData([]);
      setIsLoading(false);
      toast.error(err.message, {
        autoClose: 1000,
        position: 'top-right',
      });
    }
  };

  useEffect(() => {
  const isDemoMode = localStorage.getItem("demoMode") === "true";

  if (isDemoMode) {
    setPayoutData(demoPayoutData);
  }
}, []);

const generatePayslip = async () => {
  if (!selectedUsers) return;

  setButtonLoader(true);

  const payload = {
    users: selectedUsers,
    month: selectedMonth,
    year: selectedYear,
  };

  try {
    const response =
      await httpInjectorService.generatePayslip(payload);

    if (response?.status === 'success') {
      toast.success(response.message || 'Payslip generated successfully.');
      await getPayoutDetails();
    } else {
      toast.error(response?.message || 'Failed to generate payslip.');
    }
  } catch (err) {
    toast.error(err?.message || 'An unexpected error occurred.');
  } finally {
    setButtonLoader(false);
  }
};


  useEffect(() => {
    if (activeTab === '4') {
      getPayoutDetails();
    }
  }, [activeTab, selectedMonth, selectedYear]);

  return (
    <React.Fragment>
      {isloading ? (
        <div>
          <BulletList />
        </div>
      ) : (
        <div className="mt-3">
          <Button
            float="right"
            className="mx-3"
            rounded="3"
            colorScheme="purple"
            isLoading={buttonLoader}
            onClick={generatePayslip}
          >
            Generate Payslip
          </Button>
          <div className="row">
            <div className="col-sm-2">
              <Label>
                Select Month <span className="text-danger">*</span>
              </Label>
              <select
                className="form-control"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {months.map((month) => (
                  <option key={month} value={month}>
                    {month}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-sm-2">
              <Label>
                Select Year <span className="text-danger">*</span>
              </Label>
              <select
                className="form-control"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {getPayoutData.length === 0 ? (
            <Empty />
          ) : (
            <div className="mt-3">
              <TableContainer
                columns={columns}
                data={getPayoutData}
                isGlobalFilter={true}
                customPageSize={10}
                className="custom-header-css"
              />
            </div>
          )}
        </div>
      )}
    </React.Fragment>
  );
};

export default Payout;